import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CircleDollarSign, Clock3, ListChecks, Minus, SquareTerminal, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';

import { BarChart, type BarDatum } from '../components/BarChart';
import { BILLING_PATH, ErrorCallout, PageHead, Segmented, SkeletonRows } from '../components/ui';
import { dayLabel, hours, money, moneyFine, monthName, plural } from '../lib/format';
import { useDocumentTitle, useResource } from '../lib/hooks';
import { useBilling } from '../lib/session';
import type { Usage, UsageDay } from '../lib/types';

type Period = 'month' | '7' | '30' | '90';
const PERIODS: { value: Period; label: string }[] = [
  { value: 'month', label: 'This month' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

type Measure = 'spend' | 'time';
const MEASURES: { value: Measure; label: string }[] = [
  { value: 'spend', label: 'Spend' },
  { value: 'time', label: 'Mac time' },
];

const DAY_MS = 86_400_000;
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * The plane answers windows that end today (UTC), up to 90 days. A period is
 * one of those, plus a longer one that also holds the stretch before it, to
 * compare against: the same days of last month, or the prior 7 or 30 days.
 */
function windowFor(period: Period, now = Date.now()) {
  const d = new Date(now);
  const today = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  if (period === 'month') {
    const dayOfMonth = d.getUTCDate();
    const prevStart = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1);
    const prevLength = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)).getUTCDate();
    const prev = isoDay(prevStart).slice(0, 7);
    const upTo = Math.min(dayOfMonth, prevLength);
    return {
      days: dayOfMonth,
      compareDays: dayOfMonth + prevLength,
      inPrior: (date: string) => date.startsWith(prev) && Number(date.slice(8)) <= upTo,
      against: upTo === 1 ? dayLabel(`${prev}-01`) : `${dayLabel(`${prev}-01`)}–${upTo}`,
    };
  }
  const days = Number(period);
  const start = isoDay(today - (days - 1) * DAY_MS);
  return {
    days,
    compareDays: days * 2 <= 90 ? days * 2 : null,
    inPrior: (date: string) => date < start,
    against: `prior ${days} days`,
  };
}

interface Sums {
  seconds: number;
  costUsd: number;
  tasks: number;
  sandboxes: number;
}
const sum = (rows: UsageDay[]): Sums =>
  rows.reduce((t, r) => ({ seconds: t.seconds + r.seconds, costUsd: t.costUsd + r.costUsd, tasks: t.tasks + r.tasks, sandboxes: t.sandboxes + r.sandboxes }), { seconds: 0, costUsd: 0, tasks: 0, sandboxes: 0 });

function compactUsd(v: number): string {
  if (v === 0) return '$0';
  if (v < 1) return `$${v.toFixed(v < 0.1 ? 3 : 2).replace(/0+$/, '').replace(/\.$/, '')}`;
  return `$${v.toFixed(v < 10 ? 1 : 0).replace(/\.0$/, '')}`;
}
function compactHours(v: number): string {
  if (v === 0) return '0 h';
  return `${v < 1 ? v.toFixed(1).replace(/^0/, '0') : Number.isInteger(v) ? v : v.toFixed(1)} h`;
}
const count = (n: number) => n.toLocaleString('en-US');
const perDay = (n: number) => (n < 10 ? n.toFixed(1) : count(Math.round(n)));

/** "+12.4% vs prior 30 days"; with nothing to compare against, the daily average instead. */
function Change({ now, prior, against, average }: { now: number; prior: number | undefined; against: string; average: string }) {
  if (prior === undefined) return <span className="usage__delta">Avg. {average} a day</span>;
  if (prior === 0) {
    const Icon = now > 0 ? TrendingUp : Minus;
    return (
      <span className="usage__delta">
        <Icon aria-hidden="true" />
        {now > 0 ? 'None in' : 'No change vs'} <span className="nowrap">{against}</span>
      </span>
    );
  }
  const change = (now - prior) / prior;
  const pct = Math.abs(change) * 100;
  const flat = pct < 0.05;
  const Icon = flat ? Minus : change > 0 ? TrendingUp : TrendingDown;
  return (
    <span className="usage__delta">
      <Icon aria-hidden="true" />
      <b>
        {flat ? '' : change > 0 ? '+' : '−'}
        {pct >= 100 ? Math.round(pct) : pct.toFixed(1)}%
      </b>{' '}
      vs <span className="nowrap">{against}</span>
    </span>
  );
}

function Kpi({ label, icon: Icon, value, children }: { label: string; icon: LucideIcon; value: string | undefined; children?: ReactNode }) {
  return (
    <div className="usage__kpi">
      <div className="usage__kpi-label">
        {label}
        <Icon aria-hidden="true" />
      </div>
      <div className="usage__kpi-value">{value ?? <span className="skeleton" style={{ width: 96, height: 28 }} />}</div>
      <div className="usage__kpi-sub">{children ?? '\u00a0'}</div>
    </div>
  );
}

/** A thin share bar; a spending limit turns amber near it and red past it, as on Billing. */
function Meter({ share, budget = false }: { share: number; budget?: boolean }) {
  const pct = Math.min(100, Math.max(0, share * 100));
  return (
    <div className="usage__meter" aria-hidden="true">
      <span className={budget && share >= 1 ? 'is-over' : budget && share >= 0.8 ? 'is-warn' : undefined} style={{ width: `${Math.max(pct, share > 0 ? 1 : 0)}%` }} />
    </div>
  );
}

const SKELETON_BARS = [38, 52, 30, 64, 46, 72, 58, 34, 80, 50, 66, 42, 56, 74];

export function UsagePage() {
  useDocumentTitle('Usage');
  const [params, setParams] = useSearchParams();
  const period: Period = PERIODS.find((p) => p.value === params.get('period'))?.value ?? 'month';
  const [measure, setMeasure] = useState<Measure>('spend');
  const { billing } = useBilling();

  const win = useMemo(() => windowFor(period), [period]);
  const cur = useResource<Usage>(`/v1/usage?days=${win.days}`, { interval: 60_000 });
  const cmp = useResource<Usage>(win.compareDays ? `/v1/usage?days=${win.compareDays}` : null);
  const u = cur.data;
  const stale = cur.loading && !!u;

  const rows = useMemo(() => (u ? (period === 'month' ? u.daily.filter((d) => d.date.startsWith(u.period)) : u.daily) : []), [u, period]);
  const totals = useMemo(() => (u ? sum(rows) : undefined), [u, rows]);
  const prior = useMemo(() => (cmp.data && !cmp.loading && win.compareDays ? sum(cmp.data.daily.filter((d) => win.inPrior(d.date))) : undefined), [cmp.data, cmp.loading, win]);

  const chart = useMemo((): BarDatum[] => {
    const tally = (d: UsageDay) => `${plural(d.tasks, 'task')} · ${plural(d.sandboxes, 'sandbox', 'sandboxes')}`;
    const data: BarDatum[] = rows.map((d) =>
      measure === 'spend'
        ? { date: d.date, value: d.costUsd, note: `${hours(d.seconds)} · ${tally(d)}` }
        : { date: d.date, value: d.seconds / 3600, note: `${moneyFine(d.costUsd)} · ${tally(d)}` },
    );
    // This month keeps its whole length on the axis; the days still to come stay empty.
    if (u && period === 'month' && rows.length) {
      const [y, m] = u.period.split('-').map(Number);
      const length = new Date(Date.UTC(y, m, 0)).getUTCDate();
      for (let day = Number(rows[rows.length - 1].date.slice(8)) + 1; day <= length; day++) data.push({ date: `${u.period}-${String(day).padStart(2, '0')}`, value: null });
    }
    return data;
  }, [rows, measure, u, period]);

  const keyTotal = u ? u.byKey.reduce((s, k) => s + k.costUsd, 0) : 0;
  const n = Math.max(1, rows.length);
  const range = chart.length ? `${dayLabel(chart[0].date)} – ${dayLabel(chart[chart.length - 1].date)}, UTC` : '\u00a0';
  const limit = billing?.budget.limitUsd ?? null;

  return (
    <>
      <PageHead title="Usage" actions={<Segmented label="Period" options={PERIODS} value={period} onChange={(v) => setParams(v === 'month' ? {} : { period: v })} />} />
      <div className="usage">
        {cur.error ? <ErrorCallout error={cur.error} onRetry={() => void cur.reload()} /> : null}

        <section className={`usage__kpis${stale ? ' is-loading' : ''}`} aria-label="Totals">
          <Kpi label="Spend" icon={CircleDollarSign} value={totals && money(totals.costUsd)}>
            {totals ? <Change now={totals.costUsd} prior={prior?.costUsd} against={win.against} average={money(totals.costUsd / n)} /> : null}
          </Kpi>
          <Kpi label="Mac time" icon={Clock3} value={totals && hours(totals.seconds)}>
            {totals ? <Change now={totals.seconds} prior={prior?.seconds} against={win.against} average={hours(totals.seconds / n)} /> : null}
          </Kpi>
          <Kpi label="Sandboxes" icon={SquareTerminal} value={totals && count(totals.sandboxes)}>
            {totals ? <Change now={totals.sandboxes} prior={prior?.sandboxes} against={win.against} average={perDay(totals.sandboxes / n)} /> : null}
          </Kpi>
          <Kpi label="Tasks" icon={ListChecks} value={totals && count(totals.tasks)}>
            {totals ? <Change now={totals.tasks} prior={prior?.tasks} against={win.against} average={perDay(totals.tasks / n)} /> : null}
          </Kpi>
        </section>

        <section className="usage__card" aria-labelledby="usage-chart-title">
          <div className="usage__card-head">
            <div>
              <h2 className="h2" id="usage-chart-title">
                {measure === 'spend' ? 'Daily spend' : 'Daily Mac time'}
              </h2>
              <p className="usage__sub">{range}</p>
            </div>
            <Segmented label="Measure" options={MEASURES} value={measure} onChange={setMeasure} />
          </div>
          {u ? (
            measure === 'spend' ? (
              <BarChart data={chart} label="Daily spend" formatValue={moneyFine} formatTick={compactUsd} height={260} loading={stale} emptyText="No usage in this period" />
            ) : (
              <BarChart data={chart} label="Daily Mac time" formatValue={(v) => hours(v * 3600)} formatTick={compactHours} height={260} loading={stale} emptyText="No usage in this period" />
            )
          ) : (
            <div className="usage__chart-skel" aria-hidden="true">
              {SKELETON_BARS.map((h, i) => (
                <span key={i} className="skeleton" style={{ height: `${h}%` }} />
              ))}
            </div>
          )}
        </section>

        <div className="usage__split">
          <section className={`usage__card usage__card--flush${stale ? ' is-loading' : ''}`} aria-labelledby="usage-keys-title">
            <div className="usage__card-head">
              <h2 className="h2" id="usage-keys-title">
                By key
              </h2>
              <Link className="small" to="/keys">
                API keys
              </Link>
            </div>
            {u && u.byKey.length === 0 ? (
              <p className="usage__none">No key has used Mac time in this period.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Key</th>
                      <th scope="col" className="r">
                        Mac time
                      </th>
                      <th scope="col" className="r">
                        Spend
                      </th>
                      <th scope="col" style={{ width: '30%' }}>
                        Share
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {u ? (
                      u.byKey.map((k) => {
                        const share = keyTotal > 0 ? k.costUsd / keyTotal : 0;
                        return (
                          <tr key={k.keyId ?? k.name}>
                            <td className="m-title">
                              <span className="cell-trunc usage__key">{k.name}</span>
                            </td>
                            <td className="r num m-hide">
                              {hours(k.seconds)}
                            </td>
                            <td className="r num m-end">{money(k.costUsd)}</td>
                            <td className="m-full">
                              <div className="usage__share">
                                <div className="usage__meter">
                                  <span style={{ width: `${Math.max(1, share * 100)}%` }} />
                                </div>
                                <span className="num">{Math.round(share * 100)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <SkeletonRows cols={4} rows={3} />
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="usage__card" aria-labelledby="usage-month-title">
            <div className="usage__card-head">
              <h2 className="h2" id="usage-month-title">
                {billing ? monthName(billing.period.id) : 'This month'}
              </h2>
              <Link className="small" to={BILLING_PATH}>
                Billing
              </Link>
            </div>
            {billing ? (
              <dl className="usage__facts">
                <div>
                  <dt>Spending limit</dt>
                  <dd>
                    {limit !== null ? (
                      <>
                        <b>{money(billing.budget.spentUsd)}</b> of <Link to={`${BILLING_PATH}#limit`}>{money(limit)}</Link>
                      </>
                    ) : (
                      <Link to={`${BILLING_PATH}#limit`}>Set a limit</Link>
                    )}
                  </dd>
                  {limit ? <Meter share={billing.budget.spentUsd / limit} budget /> : null}
                </div>
                {billing.credit.grantedUsd > 0 ? (
                  <div>
                    <dt>Credit</dt>
                    <dd>
                      <b>{money(billing.credit.usedUsd)}</b> of {money(billing.credit.grantedUsd)} used
                    </dd>
                    <Meter share={billing.credit.usedUsd / billing.credit.grantedUsd} />
                  </div>
                ) : null}
                <div>
                  <dt>Estimated invoice</dt>
                  <dd>
                    <b>{money(billing.period.estimatedInvoiceUsd)}</b>
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="usage__facts" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="skeleton" style={{ width: `${70 - i * 14}%` }} />
                ))}
              </div>
            )}
          </section>
        </div>

        <p className="usage__note">
          Billed per second{billing ? ` at ${money(billing.plan.usdPerHour)} an hour` : ''}. <Link to="/docs#pricing">Pricing</Link>
        </p>
      </div>
    </>
  );
}
